/**
 * Discover More panel (ECSR-1309)
 *
 * One slide-out panel serves every Discover More card on the product page.
 * Clicking a card fills the panel from that card's JSON
 * (script[data-discover-more-data="<handle>"], rendered by
 * snippets/discover-more-cards.liquid) and opens it through the theme's own
 * drawer (theme.Drawers), which supplies the overlay, close on X / outside
 * click / Esc, scroll lock and focus return to the card.
 */
(function() {
  'use strict';

  var PANEL_ID = 'DiscoverMorePanel';
  var MOBILE_QUERY = '(max-width: 768px)';
  // theme.Drawers returns focus to the card 500ms after closing.
  var CLOSE_SETTLE_MS = 520;

  var drawer = null;
  var panel = null;
  var lastClosedAt = 0;
  // Page scroll position taken just before the panel opens. The theme adds
  // its drawer-open class (overflow: hidden) before its scroll lock reads
  // window.scrollY; where the browser shows a classic scrollbar, hiding it
  // reflows the page wider and scroll anchoring shifts scrollY, so the lock
  // would restore a shifted position on close. We restore this one instead.
  var scrollYBeforeOpen = null;

  function openPanel(data, evt, trigger) {
    fill(data);
    scrollYBeforeOpen = window.pageYOffset;
    if (evt) {
      drawer.open(evt);
    } else {
      drawer.open(null, trigger);
    }
  }

  function readData(handle) {
    var scripts = document.querySelectorAll('script[data-discover-more-data]');
    for (var i = 0; i < scripts.length; i++) {
      if (scripts[i].getAttribute('data-discover-more-data') === handle) {
        try {
          return JSON.parse(scripts[i].textContent);
        } catch (err) {
          return null;
        }
      }
    }
    return null;
  }

  function setText(el, value) {
    el.textContent = value || '';
    el.hidden = !value;
  }

  function buildPicture(desktop, mobile) {
    var main = desktop || mobile;
    if (!main || !main.src) return null;

    var picture = document.createElement('picture');

    if (desktop && mobile && mobile.src && mobile.src !== desktop.src) {
      var source = document.createElement('source');
      source.media = MOBILE_QUERY;
      source.srcset = mobile.src;
      if (mobile.width) source.width = mobile.width;
      if (mobile.height) source.height = mobile.height;
      picture.appendChild(source);
    }

    var img = document.createElement('img');
    img.src = main.src;
    img.alt = main.alt || '';
    if (main.width) img.width = main.width;
    if (main.height) img.height = main.height;
    img.loading = 'lazy';
    img.className = 'discover-more-panel__image';
    picture.appendChild(img);

    return picture;
  }

  function fill(data) {
    var label = panel.querySelector('[data-discover-more-panel-label]');
    var heading = panel.querySelector('[data-discover-more-panel-heading]');
    var body = panel.querySelector('[data-discover-more-panel-body]');
    var link = panel.querySelector('[data-discover-more-panel-link]');
    var divider = panel.querySelector('[data-discover-more-panel-divider]');
    var media = panel.querySelector('[data-discover-more-panel-media]');

    setText(label, data.headerLabel);
    setText(heading, data.heading);

    body.innerHTML = data.bodyHtml || '';
    body.hidden = !data.bodyHtml;

    if (data.linkUrl && data.linkText) {
      link.href = data.linkUrl;
      link.textContent = data.linkText;
      link.hidden = false;
    } else {
      link.removeAttribute('href');
      link.textContent = '';
      link.hidden = true;
    }

    media.innerHTML = '';
    var picture = buildPicture(data.desktopImage, data.mobileImage);
    if (picture) media.appendChild(picture);
    media.hidden = !picture;
    divider.hidden = !picture;

    panel.setAttribute('aria-labelledby', data.heading ? heading.id : label.id);
  }

  // theme.a11y.trapFocus only wraps Tab forward from the last element; this
  // keeps Tab and Shift+Tab cycling inside the panel.
  function focusableInPanel() {
    return [].slice.call(
      panel.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex^="-"])')
    ).filter(function(el) {
      return !el.hidden && el.offsetParent !== null;
    });
  }

  function isOpen() {
    return !!(drawer && drawer.isOpen && panel);
  }

  function onKeydown(evt) {
    if (evt.key !== 'Tab' || !isOpen()) return;

    var focusable = focusableInPanel();
    if (!focusable.length) return;

    // Move focus ourselves rather than relying on document tab order, which
    // third-party widgets with a positive tabindex can pull out of the panel.
    evt.preventDefault();
    var index = focusable.indexOf(document.activeElement);
    var next;
    if (evt.shiftKey) {
      next = index <= 0 ? focusable.length - 1 : index - 1;
    } else {
      next = index === -1 || index === focusable.length - 1 ? 0 : index + 1;
    }
    focusable[next].focus();
  }

  // Third-party widgets can still pull focus out (e.g. skip links injected on
  // the first Tab); bring it straight back while the panel is open.
  function onFocusin(evt) {
    if (!isOpen() || panel.contains(evt.target)) return;
    var focusable = focusableInPanel();
    (focusable[0] || panel).focus();
  }

  function onTriggerClick(evt) {
    var trigger = evt.currentTarget;
    var data = readData(trigger.getAttribute('data-discover-more-handle'));
    if (!data || !drawer) return;

    evt.preventDefault();
    evt.stopPropagation();

    var wait = CLOSE_SETTLE_MS - (Date.now() - lastClosedAt);
    if (wait > 0) {
      // Let the previous close finish returning focus before reopening.
      window.setTimeout(function() {
        openPanel(data, null, trigger);
      }, wait);
      return;
    }

    openPanel(data, evt);
  }

  function init() {
    if (!window.theme || !theme.Drawers) return;

    var fresh = [].slice.call(document.querySelectorAll('[data-discover-more-panel]')).filter(function(el) {
      return el.parentNode !== document.body;
    });
    if (!fresh.length) return;

    // Keep a single panel, attached to <body> so no ancestor transform or
    // stacking context can clip the fixed-position drawer.
    if (panel && panel.parentNode === document.body) {
      if (drawer && drawer.isOpen) drawer.close();
      panel.parentNode.removeChild(panel);
    }
    panel = fresh[0];
    for (var i = 1; i < fresh.length; i++) {
      fresh[i].parentNode.removeChild(fresh[i]);
    }
    document.body.appendChild(panel);

    drawer = new theme.Drawers(PANEL_ID, 'discover-more');

    document.querySelectorAll('[data-discover-more-trigger]').forEach(function(trigger) {
      if (trigger.hasAttribute('data-discover-more-bound')) return;
      trigger.setAttribute('data-discover-more-bound', '');
      trigger.addEventListener('click', onTriggerClick);
    });
  }

  document.addEventListener('keydown', onKeydown, true);
  document.addEventListener('focusin', onFocusin, true);

  document.addEventListener('drawerClose.' + PANEL_ID, function() {
    lastClosedAt = Date.now();

    var y = scrollYBeforeOpen;
    scrollYBeforeOpen = null;
    if (y === null) return;
    // The theme releases its scroll lock right after this event, in the same
    // call; restore once that has finished and before the next paint.
    Promise.resolve().then(function() {
      if (document.body.style.position === 'fixed') return; // another lock is still active
      window.scrollTo(0, y);
    });
  });

  // Theme editor: the product section re-renders with fresh markup.
  document.addEventListener('shopify:section:load', init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
